/**
 * VisualGest (Olifel) — Settings panel (Fase O.4 — 2026-05-14).
 *
 * Renderizado em settings/index.js quando `invoice_platform_id === 2`.
 * Encapsula todo o flow Olifel sem inflar o componente principal:
 *   - Form connect (email/password/empresa) ou estado ligado
 *   - Dropdowns Armazém + Location (cascading) e Família
 *   - Preferências: Estratégia de stock + Preço primário + Tax code default
 *   - Inbound token: mostrado plain 1× após connect/regenerate, depois só last4
 *   - Initial sync: barra de progresso + start/cancel
 *
 * UI emparelhada com o estilo Moloni existente (mesmos col-md-* + reactstrap).
 * Strings em t() com defaultValue PT para evitar quebra antes de i18n estar feita.
 */

import React, { useEffect, useState } from "react";
import {
    Button,
    Form,
    FormGroup,
    Label,
    Input,
} from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import {
    connectOlifel,
    disconnectOlifel,
    getOlifelStatus,
    olifelAuthTest,
    regenerateOlifelInboundToken,
    startOlifelInitialSync,
    getOlifelInitialSyncStatus,
    cancelOlifelInitialSync,
    getOlifelWarehouses,
    getOlifelFamilies,
    refreshOlifelWarehouses,
    refreshOlifelFamilies,
} from "../../services/OlifelServices";

const TAX_CODE_OPTIONS = [
    { value: "NOR", label: "NOR — Normal (23%)" },
    { value: "INT", label: "INT — Intermédio" },
    { value: "RED", label: "RED — Reduzido" },
    { value: "ISE", label: "ISE — Isento" },
];

const PRICE_OPTIONS = [
    { value: 1, label: "Preço 1 (venda)" },
    { value: 2, label: "Preço 2 (original)" },
];

const STRATEGY_OPTIONS = [
    {
        value: "on_invoice",
        label: "On invoice (recomendado) — movimento só ao facturar",
    },
    {
        value: "on_sale",
        label: "On sale — movimento imediato ao aceitar venda",
    },
];

export default function VisualGestSettingsPanel() {
    const { t } = useTranslation();

    // ── Estado da ligação ────────────────────────────────────────
    const [status, setStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [initSync, setInitSync] = useState(null);

    // ── Form connect ─────────────────────────────────────────────
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [empresa, setEmpresa] = useState("");
    const [connecting, setConnecting] = useState(false);
    const [authTesting, setAuthTesting] = useState(false);

    // ── Inbound token (mostrado 1× após connect/regenerate) ──────
    const [inboundTokenPlain, setInboundTokenPlain] = useState(null);
    const [regenerating, setRegenerating] = useState(false);

    // ── Listas dropdown ──────────────────────────────────────────
    const [warehouses, setWarehouses] = useState([]);
    const [families, setFamilies] = useState([]);
    const [loadingLists, setLoadingLists] = useState(false);

    // ── Form defaults ────────────────────────────────────────────
    const [warehouseCode, setWarehouseCode] = useState("");
    const [locationCode, setLocationCode] = useState("");
    const [defaultFamilia, setDefaultFamilia] = useState(null);
    const [defaultTaxCode, setDefaultTaxCode] = useState("NOR");
    const [defaultPriceId, setDefaultPriceId] = useState(1);
    const [movementStrategy, setMovementStrategy] = useState("on_invoice");

    // Emissão de documentos — o VG exige uma classe de cliente configurada
    // (código fornecido pela Olifel) e um código postal de fallback para
    // vendas sem morada (consumidor final).
    const [doctypeInvoice, setDoctypeInvoice] = useState("");
    const [defaultClientClass, setDefaultClientClass] = useState("");
    const [consumerFinalPostal, setConsumerFinalPostal] = useState("");

    // Produtos especiais — códigos VG de artigos pré-criados no portal
    // VisualGest (Transporte, Outra Parcela, Embalagem, Taxa Desmantelamento).
    // Input livre porque a API VG não tem endpoint para procurar produtos
    // por nome — admin copia o código do portal VG.
    const [shippingCode, setShippingCode] = useState("");
    const [packagingCode, setPackagingCode] = useState("");
    const [otherParcelCode, setOtherParcelCode] = useState("");
    const [dismantleTaxCode, setDismantleTaxCode] = useState("");
    const [shippingValue, setShippingValue] = useState("");
    const [packagingValue, setPackagingValue] = useState("");
    const [otherParcelValue, setOtherParcelValue] = useState("");
    const [dismantleTaxValue, setDismantleTaxValue] = useState("");

    const connected = status && status.connected;
    const settings = (status && status.settings) || {};

    // ── Boot ─────────────────────────────────────────────────────
    const refreshStatus = async () => {
        try {
            setLoadingStatus(true);
            const r = await getOlifelStatus();
            const data = r.data;
            setStatus(data);
            const s = (data && data.settings) || {};
            // Pre-popula form com settings actuais (read-only enquanto ligado).
            setEmail(s.olifel_email || "");
            setEmpresa(s.olifel_empresa || "");
            setWarehouseCode(s.olifel_warehouse || "");
            setLocationCode(s.olifel_location || "");
            setDefaultFamilia(s.olifel_default_familia || null);
            setDefaultTaxCode(s.olifel_default_tax_code || "NOR");
            setDefaultPriceId(Number(s.olifel_default_price_id) || 1);
            setMovementStrategy(s.olifel_movement_strategy || "on_invoice");
            setDoctypeInvoice(s.olifel_doctype_invoice || "");
            setDefaultClientClass(s.olifel_default_client_class || "");
            setConsumerFinalPostal(s.olifel_consumer_final_postal || "");
            setShippingCode(s.olifel_shipping_code || "");
            setPackagingCode(s.olifel_packaging_code || "");
            setOtherParcelCode(s.olifel_other_parcel_code || "");
            setDismantleTaxCode(s.olifel_dismantle_tax_code || "");
            setShippingValue(s.olifel_total_value_shipping || "");
            setPackagingValue(s.olifel_total_value_packaging || "");
            setOtherParcelValue(s.olifel_total_value_other_parcel || "");
            setDismantleTaxValue(s.olifel_total_value_dismantle_tax || "");
        } catch (err) {
            // Status falha silenciosa — provavelmente não tem token ainda
            setStatus(null);
        } finally {
            setLoadingStatus(false);
        }
    };

    const refreshInitSync = async () => {
        try {
            const r = await getOlifelInitialSyncStatus();
            setInitSync(r.data || null);
        } catch (e) {
            setInitSync(null);
        }
    };

    const loadLists = async ({ forceRefresh = false } = {}) => {
        if (!connected) return;
        try {
            setLoadingLists(true);
            const [wResp, fResp] = await Promise.all([
                getOlifelWarehouses({ refresh: forceRefresh }),
                getOlifelFamilies({ refresh: forceRefresh }),
            ]);
            setWarehouses((wResp.data && wResp.data.warehouses) || []);
            setFamilies((fResp.data && fResp.data.families) || []);
        } catch (err) {
            toast.error(
                t("visualgest_lists_load_failed", {
                    defaultValue: "Falha a obter armazéns/famílias VG",
                }),
            );
        } finally {
            setLoadingLists(false);
        }
    };

    useEffect(() => {
        refreshStatus();
        refreshInitSync();
    }, []);

    useEffect(() => {
        if (connected) loadLists();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected]);

    // Polling initial-sync enquanto running
    useEffect(() => {
        if (initSync?.status !== "running") return;
        const id = setInterval(refreshInitSync, 5000);
        return () => clearInterval(id);
    }, [initSync?.status]);

    // ── Handlers ─────────────────────────────────────────────────
    const handleConnect = async () => {
        if (!email || !password) {
            toast.error(
                t("visualgest_creds_required", {
                    defaultValue: "Email e password VisualGest obrigatórios",
                }),
            );
            return;
        }
        try {
            setConnecting(true);
            const payload = {
                email: email.trim(),
                password,
                empresa: empresa.trim() || undefined,
            };
            const r = await connectOlifel(payload);
            const data = r.data || {};
            if (!data.success) {
                toast.error(data.message || "Falha a ligar VisualGest");
                return;
            }
            // Token inbound aparece UMA única vez
            if (data.inbound_token) {
                setInboundTokenPlain(data.inbound_token);
            }
            // Limpa o campo password (segurança UX)
            setPassword("");
            toast.success(
                t("visualgest_connected", {
                    defaultValue: "VisualGest ligado",
                }),
            );
            await refreshStatus();
        } catch (err) {
            const msg =
                (err.response && err.response.data && err.response.data.message) ||
                err.message;
            toast.error(msg || "Erro a ligar VisualGest");
        } finally {
            setConnecting(false);
        }
    };

    const handleDisconnect = async ({ purge = false } = {}) => {
        if (!window.confirm(
            purge
                ? t("visualgest_disconnect_purge_confirm", {
                      defaultValue:
                          "Desligar VisualGest E APAGAR credenciais? Será necessário reintroduzir email/password para reconectar.",
                  })
                : t("visualgest_disconnect_confirm", {
                      defaultValue:
                          "Desligar VisualGest? Mantém credenciais para reconnect rápido.",
                  }),
        )) {
            return;
        }
        try {
            await disconnectOlifel({ purge });
            toast.success(
                t("visualgest_disconnected", {
                    defaultValue: "VisualGest desligado",
                }),
            );
            setInboundTokenPlain(null);
            await refreshStatus();
        } catch (err) {
            toast.error(err.message || "Erro a desligar");
        }
    };

    const handleAuthTest = async () => {
        try {
            setAuthTesting(true);
            const r = await olifelAuthTest();
            if (r.data.success) {
                toast.success(
                    t("visualgest_auth_ok", {
                        defaultValue: `Credenciais OK (${r.data.sample_size ?? "?"} armazéns)`,
                    }),
                );
            } else {
                toast.error(r.data.message || "Falha auth-test");
            }
        } catch (err) {
            const msg =
                (err.response && err.response.data && err.response.data.message) ||
                err.message;
            toast.error(msg || "Erro auth-test");
        } finally {
            setAuthTesting(false);
        }
    };

    const handleRegenerateInboundToken = async () => {
        if (!window.confirm(
            t("visualgest_inbound_regenerate_confirm", {
                defaultValue:
                    "Gerar novo token inbound? O token anterior continua a aceitar pedidos VG durante 24 horas (janela de rotação sem downtime). Tens de actualizar o token no painel VisualGest dentro desse prazo.",
            }),
        )) {
            return;
        }
        try {
            setRegenerating(true);
            const r = await regenerateOlifelInboundToken();
            if (r.data.success) {
                setInboundTokenPlain(r.data.inbound_token);
                toast.success(
                    t("visualgest_inbound_regenerated", {
                        defaultValue: "Novo token inbound gerado",
                    }),
                );
                await refreshStatus();
            }
        } catch (err) {
            toast.error(err.message || "Erro a regenerar token");
        } finally {
            setRegenerating(false);
        }
    };

    const handleRefreshLists = async () => {
        await Promise.all([refreshOlifelWarehouses(), refreshOlifelFamilies()]);
        await loadLists({ forceRefresh: false });
        toast.success(
            t("visualgest_lists_refreshed", {
                defaultValue: "Listas VG actualizadas",
            }),
        );
    };

    const handleStartSync = async () => {
        try {
            await startOlifelInitialSync();
            toast.success(
                t("visualgest_initsync_started", {
                    defaultValue: "Sincronização inicial iniciada",
                }),
            );
            await refreshInitSync();
        } catch (err) {
            const msg =
                (err.response && err.response.data && err.response.data.message) ||
                err.message;
            toast.error(msg || "Erro a iniciar sync");
        }
    };

    const handleCancelSync = async () => {
        try {
            await cancelOlifelInitialSync();
            await refreshInitSync();
        } catch (err) {
            // ignore
        }
    };

    // ── Computed ─────────────────────────────────────────────────
    const warehouseOptions = (() => {
        const seen = new Set();
        const out = [];
        for (const w of warehouses) {
            if (seen.has(w.warehouse_code)) continue;
            seen.add(w.warehouse_code);
            out.push({
                value: w.warehouse_code,
                label: `${w.warehouse_code} — ${w.warehouse_name || ""}`,
            });
        }
        return out;
    })();

    const locationOptions = warehouses
        .filter((w) => w.warehouse_code === warehouseCode && w.location_code)
        .map((w) => ({
            value: w.location_code,
            label: `${w.location_code} — ${w.location_name || ""}`,
        }));

    const familyOptions = families.map((f) => ({
        value: f.familia_code,
        label: `${f.familia_code} — ${f.familia_desc || ""}`,
    }));

    // ── Render ───────────────────────────────────────────────────
    return (
        <div className="col-md-12" style={{ marginTop: 20 }}>
            <div
                style={{
                    border: "1px solid #c8e6f7",
                    borderRadius: 8,
                    padding: 14,
                    backgroundColor: "#f7fbfd",
                    marginBottom: 16,
                }}
            >
                <h4
                    style={{
                        fontWeight: "bold",
                        fontSize: 16,
                        marginBottom: 12,
                        color: "#0c5460",
                    }}
                >
                    {t("visualgest_config_title", {
                        defaultValue: "Configuração VisualGest",
                    })}
                </h4>

                {/* ── Estado ligação ─────────────────────────────── */}
                {loadingStatus && (
                    <div style={{ color: "#6c757d" }}>
                        {t("loading", { defaultValue: "A carregar..." })}
                    </div>
                )}

                {!loadingStatus && !connected && (
                    <Form>
                        <p style={{ marginBottom: 12, color: "#6c757d", fontSize: 13 }}>
                            {t("visualgest_connect_intro", {
                                defaultValue:
                                    "Introduz as credenciais do utilizador VisualGest dedicado à integração. Quando ligares, será forçado sync_all=1 (sincronização total das peças activas).",
                            })}
                        </p>
                        <div className="row">
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>{t("visualgest_email", { defaultValue: "Email VG" })}</Label>
                                    <Input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="utilizador@example.com"
                                        autoComplete="off"
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>{t("visualgest_password", { defaultValue: "Password VG" })}</Label>
                                    <Input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        autoComplete="new-password"
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>
                                        {t("visualgest_empresa", { defaultValue: "Empresa (código, opcional)" })}
                                    </Label>
                                    <Input
                                        type="text"
                                        value={empresa}
                                        onChange={(e) => setEmpresa(e.target.value)}
                                        placeholder="001"
                                    />
                                    <small style={{ color: "#6c757d" }}>
                                        {t("visualgest_empresa_help", {
                                            defaultValue:
                                                "Se vazio, usamos a empresa por defeito da conta VisualGest.",
                                        })}
                                    </small>
                                </FormGroup>
                            </div>
                        </div>
                        <Button color="primary" onClick={handleConnect} disabled={connecting}>
                            {connecting
                                ? t("connecting", { defaultValue: "A ligar..." })
                                : t("visualgest_connect", { defaultValue: "Ligar VisualGest" })}
                        </Button>
                    </Form>
                )}

                {connected && (
                    <>
                        {/* ── Status ─────────────────────────────────── */}
                        <div
                            style={{
                                backgroundColor: "#d4edda",
                                border: "1px solid #c3e6cb",
                                color: "#155724",
                                padding: 10,
                                borderRadius: 4,
                                marginBottom: 12,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 8,
                            }}
                        >
                            <div>
                                <strong>
                                    {t("visualgest_connected_short", { defaultValue: "Ligado" })}
                                </strong>{" "}
                                · {settings.olifel_email || ""} ·{" "}
                                {t("visualgest_empresa_label", { defaultValue: "Empresa" })}{" "}
                                <strong>{settings.olifel_empresa || "-"}</strong>
                                {status.expires_at && (
                                    <span style={{ marginLeft: 10, fontSize: 12, color: "#6c757d" }}>
                                        {t("visualgest_expires", { defaultValue: "Token expira" })}:{" "}
                                        {new Date(status.expires_at).toLocaleString()}
                                    </span>
                                )}
                            </div>
                            <div style={{ display: "flex", gap: 6 }}>
                                <Button color="secondary" size="sm" onClick={handleAuthTest} disabled={authTesting}>
                                    {authTesting
                                        ? "..."
                                        : t("visualgest_auth_test", { defaultValue: "Testar credenciais" })}
                                </Button>
                                <Button color="warning" size="sm" onClick={() => handleDisconnect({ purge: false })}>
                                    {t("visualgest_disconnect", { defaultValue: "Desligar" })}
                                </Button>
                                <Button color="danger" size="sm" onClick={() => handleDisconnect({ purge: true })}>
                                    {t("visualgest_disconnect_purge", { defaultValue: "Desligar+Apagar" })}
                                </Button>
                            </div>
                        </div>

                        {status.expires_warning && (
                            <div
                                style={{
                                    backgroundColor: "#fff3cd",
                                    border: "1px solid #ffeeba",
                                    color: "#856404",
                                    padding: 10,
                                    borderRadius: 4,
                                    marginBottom: 12,
                                }}
                            >
                                {t("visualgest_expires_warning", {
                                    defaultValue:
                                        "Token VG vai expirar em breve. Próximo relogin automático corre em 5 minutos.",
                                })}
                            </div>
                        )}

                        {/* ── Inbound token (1×) ──────────────────────── */}
                        {inboundTokenPlain && (
                            <div
                                style={{
                                    backgroundColor: "#fff3cd",
                                    border: "1px solid #ffeeba",
                                    color: "#856404",
                                    padding: 12,
                                    borderRadius: 4,
                                    marginBottom: 12,
                                }}
                            >
                                <strong>
                                    {t("visualgest_inbound_token_title", {
                                        defaultValue: "Token inbound (mostrado UMA única vez)",
                                    })}
                                </strong>
                                <div
                                    style={{
                                        fontFamily: "monospace",
                                        fontSize: 12,
                                        backgroundColor: "#fff",
                                        padding: 8,
                                        marginTop: 6,
                                        borderRadius: 4,
                                        wordBreak: "break-all",
                                    }}
                                >
                                    {inboundTokenPlain}
                                </div>
                                <small style={{ display: "block", marginTop: 6 }}>
                                    {t("visualgest_inbound_token_help", {
                                        defaultValue:
                                            "Copia agora — depois de saíres deste ecrã, só vês os últimos 4 caracteres. Configura este token no painel VisualGest da Olifel para que o VG possa chamar os endpoints inbound TP.",
                                    })}
                                </small>
                                <Button
                                    color="secondary"
                                    size="sm"
                                    style={{ marginTop: 8 }}
                                    onClick={() => {
                                        navigator.clipboard
                                            .writeText(inboundTokenPlain)
                                            .then(() =>
                                                toast.success(
                                                    t("copied", { defaultValue: "Copiado" }),
                                                ),
                                            );
                                    }}
                                >
                                    {t("copy", { defaultValue: "Copiar" })}
                                </Button>
                            </div>
                        )}

                        {!inboundTokenPlain && settings.olifel_inbound_token_last4 && (
                            <div
                                style={{
                                    backgroundColor: "#e9ecef",
                                    padding: 10,
                                    borderRadius: 4,
                                    marginBottom: 12,
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <span>
                                        {t("visualgest_inbound_token_short", {
                                            defaultValue: "Token inbound activo",
                                        })}
                                        : <code>****{settings.olifel_inbound_token_last4}</code>
                                    </span>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={handleRegenerateInboundToken}
                                        disabled={regenerating}
                                    >
                                        {regenerating
                                            ? "..."
                                            : t("visualgest_inbound_regenerate", {
                                                  defaultValue: "Regenerar token",
                                              })}
                                    </Button>
                                </div>

                                {/* Dual-token rotation window — token anterior ainda activo */}
                                {status.inbound_token_rotation &&
                                    status.inbound_token_rotation.previous_active && (
                                        <div
                                            style={{
                                                marginTop: 8,
                                                padding: 8,
                                                borderRadius: 4,
                                                backgroundColor: "#fff8e1",
                                                border: "1px solid #ffe0b2",
                                                color: "#5d4037",
                                                fontSize: 12,
                                            }}
                                        >
                                            <strong>
                                                {t("visualgest_rotation_active_title", {
                                                    defaultValue: "Janela de rotação activa",
                                                })}
                                            </strong>{" "}
                                            — {t("visualgest_rotation_active_body", {
                                                defaultValue:
                                                    "O token anterior continua a aceitar pedidos do VisualGest até",
                                            })}{" "}
                                            <strong>
                                                {new Date(
                                                    status.inbound_token_rotation.previous_expires_at,
                                                ).toLocaleString()}
                                            </strong>
                                            {status.inbound_token_rotation.previous_last4 && (
                                                <>
                                                    {" "}(****{status.inbound_token_rotation.previous_last4})
                                                </>
                                            )}
                                            . {t("visualgest_rotation_active_action", {
                                                defaultValue:
                                                    "Actualiza o token no painel VisualGest antes desse momento.",
                                            })}
                                        </div>
                                    )}
                            </div>
                        )}

                        {/* ── Defaults: armazém / família / strategy / preço / tax ─── */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: 6,
                                marginBottom: 6,
                            }}
                        >
                            <strong>
                                {t("visualgest_defaults_title", {
                                    defaultValue: "Configurações de sincronização",
                                })}
                            </strong>
                            <Button color="secondary" size="sm" onClick={handleRefreshLists}>
                                {t("visualgest_refresh_lists", {
                                    defaultValue: "Refrescar armazéns/famílias",
                                })}
                            </Button>
                        </div>

                        <small style={{ display: "block", color: "#6c757d", marginBottom: 10 }}>
                            {t("visualgest_defaults_help", {
                                defaultValue:
                                    "Estas escolhas guardam-se quando carregas em 'Guardar configurações' (botão geral abaixo). Todas as peças sincronizadas vão para o mesmo armazém + família default.",
                            })}
                        </small>

                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>
                                        {t("visualgest_warehouse", { defaultValue: "Armazém default" })}
                                    </Label>
                                    <Select
                                        options={warehouseOptions}
                                        isLoading={loadingLists}
                                        value={
                                            warehouseOptions.find((o) => o.value === warehouseCode) || null
                                        }
                                        onChange={(opt) => {
                                            setWarehouseCode(opt ? opt.value : "");
                                            setLocationCode("");
                                        }}
                                        placeholder={t("select", { defaultValue: "Seleccionar..." })}
                                        isClearable
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>
                                        {t("visualgest_location", { defaultValue: "Location (se aplicável)" })}
                                    </Label>
                                    <Select
                                        options={locationOptions}
                                        value={
                                            locationOptions.find((o) => o.value === locationCode) || null
                                        }
                                        onChange={(opt) => setLocationCode(opt ? opt.value : "")}
                                        placeholder={
                                            warehouseCode
                                                ? locationOptions.length === 0
                                                    ? t("visualgest_no_locations", {
                                                          defaultValue:
                                                              "Este armazém não tem locations",
                                                      })
                                                    : t("select", { defaultValue: "Seleccionar..." })
                                                : t("visualgest_select_warehouse_first", {
                                                      defaultValue: "Selecciona armazém primeiro",
                                                  })
                                        }
                                        isClearable
                                        isDisabled={!warehouseCode || locationOptions.length === 0}
                                    />
                                </FormGroup>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>
                                        {t("visualgest_default_familia", { defaultValue: "Família default" })}
                                    </Label>
                                    <Select
                                        options={familyOptions}
                                        isLoading={loadingLists}
                                        value={
                                            familyOptions.find(
                                                (o) => Number(o.value) === Number(defaultFamilia),
                                            ) || null
                                        }
                                        onChange={(opt) => setDefaultFamilia(opt ? opt.value : null)}
                                        placeholder={t("select", { defaultValue: "Seleccionar..." })}
                                        isClearable
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>
                                        {t("visualgest_default_tax_code", {
                                            defaultValue: "Tax code default",
                                        })}
                                    </Label>
                                    <Select
                                        options={TAX_CODE_OPTIONS}
                                        value={
                                            TAX_CODE_OPTIONS.find((o) => o.value === defaultTaxCode) || null
                                        }
                                        onChange={(opt) => setDefaultTaxCode(opt ? opt.value : "NOR")}
                                    />
                                </FormGroup>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>
                                        {t("visualgest_default_price", { defaultValue: "Preço primário" })}
                                    </Label>
                                    <Select
                                        options={PRICE_OPTIONS}
                                        value={
                                            PRICE_OPTIONS.find((o) => o.value === defaultPriceId) || null
                                        }
                                        onChange={(opt) => setDefaultPriceId(opt ? opt.value : 1)}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>
                                        {t("visualgest_movement_strategy", {
                                            defaultValue: "Estratégia de stock",
                                        })}
                                    </Label>
                                    <Select
                                        options={STRATEGY_OPTIONS}
                                        value={
                                            STRATEGY_OPTIONS.find(
                                                (o) => o.value === movementStrategy,
                                            ) || null
                                        }
                                        onChange={(opt) =>
                                            setMovementStrategy(opt ? opt.value : "on_invoice")
                                        }
                                    />
                                </FormGroup>
                            </div>
                        </div>

                        {/* ── Emissão de documentos ── */}
                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>
                                        {t("visualgest_doctype_invoice", {
                                            defaultValue: "Série / tipo de documento de fatura",
                                        })}
                                    </Label>
                                    <Input
                                        type="text"
                                        value={doctypeInvoice}
                                        onChange={(e) => setDoctypeInvoice(e.target.value)}
                                        placeholder="ex: FTTEL"
                                    />
                                    <small style={{ color: "#6c757d" }}>
                                        {t("visualgest_doctype_invoice_help", {
                                            defaultValue:
                                                "Código da série de fatura no VisualGest (fornecido pela Olifel). Vazio → usa o default FTTEL.",
                                        })}
                                    </small>
                                </FormGroup>
                            </div>
                        </div>

                        {/* ── Cliente / consumidor final ── */}
                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>
                                        {t("visualgest_client_class", {
                                            defaultValue: "Classe de cliente",
                                        })}
                                    </Label>
                                    <Input
                                        type="text"
                                        value={defaultClientClass}
                                        onChange={(e) => setDefaultClientClass(e.target.value)}
                                        placeholder="ex: 1"
                                    />
                                    <small style={{ color: "#6c757d" }}>
                                        {t("visualgest_client_class_help", {
                                            defaultValue:
                                                "Código da classe de cliente fornecido pela Olifel. Obrigatório para emitir facturas no VisualGest.",
                                        })}
                                    </small>
                                </FormGroup>
                            </div>
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>
                                        {t("visualgest_consumer_final_postal", {
                                            defaultValue: "Código postal consumidor final",
                                        })}
                                    </Label>
                                    <Input
                                        type="text"
                                        value={consumerFinalPostal}
                                        onChange={(e) => setConsumerFinalPostal(e.target.value)}
                                        placeholder="1000-001"
                                    />
                                    <small style={{ color: "#6c757d" }}>
                                        {t("visualgest_consumer_final_postal_help", {
                                            defaultValue:
                                                "Usado nas facturas de vendas sem morada de cliente.",
                                        })}
                                    </small>
                                </FormGroup>
                            </div>
                        </div>

                        {/* ── Produtos especiais (códigos artigo VG pré-criados) ── */}
                        <div
                            style={{
                                marginTop: 12,
                                padding: 10,
                                border: "1px solid #dee2e6",
                                borderRadius: 4,
                                backgroundColor: "#f8f9fa",
                            }}
                        >
                            <strong>
                                {t("visualgest_special_products_title", {
                                    defaultValue: "Produtos especiais para facturação",
                                })}
                            </strong>
                            <small style={{ display: "block", color: "#6c757d", marginTop: 4, marginBottom: 10 }}>
                                {t("visualgest_special_products_help", {
                                    defaultValue:
                                        "Códigos dos artigos pré-criados no portal VisualGest que serão adicionados como linhas extras nas facturas (transporte, embalagem, outra parcela, taxa de desmantelamento). Copia os códigos exactos do portal VG.",
                                })}
                            </small>
                            <div className="row">
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label>
                                            {t("visualgest_shipping_code", {
                                                defaultValue: "Transporte — código artigo VG",
                                            })}
                                        </Label>
                                        <Input
                                            type="text"
                                            value={shippingCode}
                                            onChange={(e) => setShippingCode(e.target.value)}
                                            placeholder="ex: TRANSP.001"
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label>
                                            {t("visualgest_shipping_value", {
                                                defaultValue: "Transporte — valor (€)",
                                            })}
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={shippingValue}
                                            onChange={(e) => setShippingValue(e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </FormGroup>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label>
                                            {t("visualgest_packaging_code", {
                                                defaultValue: "Embalagem — código artigo VG",
                                            })}
                                        </Label>
                                        <Input
                                            type="text"
                                            value={packagingCode}
                                            onChange={(e) => setPackagingCode(e.target.value)}
                                            placeholder="ex: EMBAL.001"
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label>
                                            {t("visualgest_packaging_value", {
                                                defaultValue: "Embalagem — valor (€)",
                                            })}
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={packagingValue}
                                            onChange={(e) => setPackagingValue(e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </FormGroup>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label>
                                            {t("visualgest_other_parcel_code", {
                                                defaultValue: "Outra parcela — código artigo VG",
                                            })}
                                        </Label>
                                        <Input
                                            type="text"
                                            value={otherParcelCode}
                                            onChange={(e) => setOtherParcelCode(e.target.value)}
                                            placeholder="ex: OUTROS.001"
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label>
                                            {t("visualgest_other_parcel_value", {
                                                defaultValue: "Outra parcela — valor (€)",
                                            })}
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={otherParcelValue}
                                            onChange={(e) => setOtherParcelValue(e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </FormGroup>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label>
                                            {t("visualgest_dismantle_tax_code", {
                                                defaultValue: "Taxa desmantelamento — código artigo VG",
                                            })}
                                        </Label>
                                        <Input
                                            type="text"
                                            value={dismantleTaxCode}
                                            onChange={(e) => setDismantleTaxCode(e.target.value)}
                                            placeholder="ex: TAXAD.001"
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label>
                                            {t("visualgest_dismantle_tax_value", {
                                                defaultValue: "Taxa desmantelamento — valor (€)",
                                            })}
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={dismantleTaxValue}
                                            onChange={(e) => setDismantleTaxValue(e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </FormGroup>
                                </div>
                            </div>
                        </div>

                        {/* ── Hidden inputs para serem submetidos no form parent ── */}
                        <input type="hidden" name="olifel_doctype_invoice" value={doctypeInvoice} />
                        <input type="hidden" name="olifel_warehouse" value={warehouseCode} />
                        <input type="hidden" name="olifel_location" value={locationCode} />
                        <input type="hidden" name="olifel_default_familia" value={defaultFamilia || ""} />
                        <input type="hidden" name="olifel_default_tax_code" value={defaultTaxCode} />
                        <input type="hidden" name="olifel_default_price_id" value={defaultPriceId} />
                        <input type="hidden" name="olifel_movement_strategy" value={movementStrategy} />
                        <input type="hidden" name="olifel_default_client_class" value={defaultClientClass} />
                        <input type="hidden" name="olifel_consumer_final_postal" value={consumerFinalPostal} />
                        <input type="hidden" name="olifel_shipping_code" value={shippingCode} />
                        <input type="hidden" name="olifel_packaging_code" value={packagingCode} />
                        <input type="hidden" name="olifel_other_parcel_code" value={otherParcelCode} />
                        <input type="hidden" name="olifel_dismantle_tax_code" value={dismantleTaxCode} />
                        <input type="hidden" name="olifel_total_value_shipping" value={shippingValue} />
                        <input type="hidden" name="olifel_total_value_packaging" value={packagingValue} />
                        <input type="hidden" name="olifel_total_value_other_parcel" value={otherParcelValue} />
                        <input type="hidden" name="olifel_total_value_dismantle_tax" value={dismantleTaxValue} />

                        {/* ── Initial sync ───────────────────────────── */}
                        <div
                            style={{
                                marginTop: 12,
                                padding: 10,
                                border: "1px solid #dee2e6",
                                borderRadius: 4,
                                backgroundColor: "#f8f9fa",
                            }}
                        >
                            <strong>
                                {t("visualgest_initsync_title", {
                                    defaultValue: "Sincronização inicial de produtos",
                                })}
                            </strong>
                            <small
                                style={{ display: "block", color: "#6c757d", marginTop: 4 }}
                            >
                                {t("visualgest_initsync_help", {
                                    defaultValue:
                                        "Envia todas as peças TP activas para o VisualGest.",
                                })}
                            </small>
                            <div style={{ marginTop: 8 }}>
                                {(!initSync || initSync.status === "idle") && (
                                    <Button color="primary" size="sm" onClick={handleStartSync}>
                                        {t("visualgest_initsync_start", {
                                            defaultValue: "Iniciar sync",
                                        })}
                                    </Button>
                                )}
                                {initSync && initSync.status === "running" && (
                                    <>
                                        <div
                                            style={{
                                                backgroundColor: "#e9ecef",
                                                height: 14,
                                                borderRadius: 4,
                                                overflow: "hidden",
                                                marginBottom: 6,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    backgroundColor: "#28a745",
                                                    height: "100%",
                                                    width: `${
                                                        initSync.total
                                                            ? Math.round(
                                                                  (100 * initSync.progress) /
                                                                      initSync.total,
                                                              )
                                                            : 0
                                                    }%`,
                                                    transition: "width 1s linear",
                                                }}
                                            />
                                        </div>
                                        <small>
                                            {initSync.progress}/{initSync.total} —{" "}
                                            {t("running", { defaultValue: "a correr" })}
                                        </small>
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            style={{ marginLeft: 8 }}
                                            onClick={handleCancelSync}
                                        >
                                            {t("cancel", { defaultValue: "Cancelar" })}
                                        </Button>
                                    </>
                                )}
                                {initSync && initSync.status === "completed" && (
                                    <div style={{ color: "#155724" }}>
                                        ✓ {t("visualgest_initsync_done", {
                                            defaultValue: "Concluída",
                                        })}{" "}
                                        ({initSync.progress}/{initSync.total})
                                        {initSync.error && (
                                            <div style={{ color: "#856404", marginTop: 4 }}>
                                                ⚠️ {initSync.error}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {initSync && initSync.status === "failed" && (
                                    <div style={{ color: "#721c24" }}>
                                        ✗ {t("failed", { defaultValue: "Falhou" })}
                                        {initSync.error && (
                                            <div style={{ fontSize: 12, marginTop: 4 }}>
                                                {initSync.error}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {initSync && initSync.status === "cancelled" && (
                                    <div style={{ color: "#856404" }}>
                                        ⊘{" "}
                                        {t("cancelled", { defaultValue: "Cancelada" })}{" "}
                                        ({initSync.progress}/{initSync.total})
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
